import { useAuth } from "@/components/AuthProvider";
import { HeadToHeadBar } from "@/components/HeadToHeadBar";
import { trainerHandle, trainerInitial } from "@/lib/trainerIdentity";

/**
 * The You-vs-them hero: two hex avatars either side of a VS badge, with the
 * head-to-head totals bar underneath.
 *
 * Extracted from Compare so the new Compare Dex page and Compare itself render
 * one implementation. This block answers "how do we compare" — it is the whole
 * point of the Compare Dex page, and it deliberately carries no trading.
 */
export function TrainerVersusPanel({
  friendName,
  friendHandle,
  friendMonogram,
  friendHue,
  friendAccountId,
}: {
  friendName: string;
  friendHandle: string;
  friendMonogram: string;
  friendHue: number;
  /**
   * The friend's ACCOUNT id (what /api/friends/:id/* expects), or null on the
   * seed/demo routes — which disables the live totals rather than fabricating
   * them.
   */
  friendAccountId: number | null;
}) {
  return (
    <section className="v2-neon-card p-4 relative overflow-hidden" data-testid="section-trainer-compare">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="v2-aurora v2-aurora--green opacity-20" />
      </div>
      <div className="relative flex items-center justify-center gap-3">
        <YouTrainerPanel />

        {/* VS hex badge */}
        <div className="flex flex-col items-center shrink-0">
          <div
            className="w-12 h-12 v2-hex-frame flex items-center justify-center"
            style={{
              background: "linear-gradient(180deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
              filter: "drop-shadow(0 0 10px var(--accent-fill-glow-strong))",
            }}
          >
            <span
              className="w-[42px] h-[42px] v2-hex-frame flex items-center justify-center v2-display-up"
              style={{
                background: "var(--surface-dark-7)",
                color: "var(--accent-fill)",
                fontSize: 11,
              }}
            >
              VS
            </span>
          </div>
        </div>

        {/* Friend panel */}
        <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
          <HexAvatar
            letter={friendMonogram}
            bg={`hsl(${friendHue} 65% 38%)`}
            textColor="#0B0E1A"
            glow={`hsl(${friendHue} 80% 55% / 0.5)`}
          />
          <div className="text-center">
            <p className="text-[13px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
              {friendName}
            </p>
            <p className="text-[11px]" style={{ color: "var(--text-ink-faint)" }}>
              {friendHandle}
            </p>
          </div>
        </div>
      </div>

      {/* You-vs-Them across every dex type (Caught / Shiny / Lucky / 100%) — the
          same head-to-head the Friends list shows. `expandable` adds the size
          dexes in their filter colours; the Friends list rows keep the
          headline four only. */}
      {friendAccountId != null && (
        <div className="relative">
          <HeadToHeadBar friendId={friendAccountId} expandable />
        </div>
      )}
    </section>
  );
}

/* ─────────── Hex avatar ─────────── */
// Renders a properly hex-clipped avatar with neon border and letter initial.
// Uses layered clip-path divs so both the border ring and fill are hex-shaped.
export function HexAvatar({
  letter,
  bg,
  textColor = "var(--accent-fill)",
  glow = "var(--accent-fill-glow)",
}: {
  letter: string;
  bg: string;
  textColor?: string;
  glow?: string;
}) {
  return (
    <div
      style={{
        width: 64,
        height: 64,
        position: "relative",
        flexShrink: 0,
        filter: `drop-shadow(0 0 10px ${glow})`,
      }}
    >
      {/* Outer hex = neon green border ring */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          clipPath: "polygon(0% 25%, 50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%)",
          background: "linear-gradient(180deg, var(--accent-fill), var(--accent-fill))",
        }}
      />
      {/* Inner hex = filled background (2px inset = border thickness) */}
      <div
        style={{
          position: "absolute",
          inset: 3,
          clipPath: "polygon(0% 25%, 50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%)",
          background: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 800,
          fontSize: 22,
          color: textColor,
        }}
      >
        {letter && letter.trim() ? letter : "?"}
      </div>
    </div>
  );
}

/* ─────────── You trainer panel ─────────── */
export function YouTrainerPanel() {
  const { account } = useAuth();
  // PoGo identity only. Never the IdP displayName.
  const letter = trainerInitial(account, "Y");
  const handle = trainerHandle(account) ?? "@you";

  return (
    <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
      <HexAvatar
        letter={letter}
        bg="var(--brand-green-on-light)"
        // Was var(--accent-fill): green ink on the bright green hex, so the
        // trainer's initial was invisible while the friend's (dark ink on a
        // mid-tone hue) read fine. --text-on-accent is the token for exactly
        // this pairing.
        textColor="var(--text-on-accent)"
        glow="var(--accent-fill-glow)"
      />
      <div className="text-center">
        <p className="text-[13px] font-bold" style={{ color: "var(--text-ink-bright)" }}>
          You
        </p>
        <p className="text-[11px]" style={{ color: "var(--text-ink-faint)" }}>
          {handle}
        </p>
      </div>
    </div>
  );
}
